import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTopicType } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { BookText, Save, AlertCircle, HelpCircle } from 'lucide-react';

// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface FormErrors {
  key?: string;
  label?: string;
  submit?: string;
}

export default function TopicTypesNewPage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to add topic types.</p>
        </div>
      </div>
    );
  }

  const [formData, setFormData] = useState({
    key: '',
    label: '',
    description: ''
  });

  const handleChange = (field: string, value: string) => {
    setFormData(f => ({ ...f, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(e => ({ ...e, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.key)) {
      newErrors.key = 'Key must be lowercase letters, numbers, and dashes only';
    }

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await createTopicType(formData);
      navigate('/admin/topic-types');
    } catch (error: any) {
      console.error('Error creating topic type:', error);
      setErrors({ submit: error.message || 'Failed to create topic type. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin/topic-types');
  };

  return (
    <div className="flex h-full">
        {/* Main Content Area */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 pl-6 pt-4">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <BookText className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-content-text">Add Topic Type</h1>
                    <p className="text-content-text-muted">Create a new Community knowledge category</p>
                  </div>
                </div>
              </div>

              {errors.submit && (
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
                  <p className="text-destructive flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {errors.submit}
                  </p>
                </div>
              )}

              <form id="topic-type-form" onSubmit={handleSubmit}>
                {/* Primary Input Fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Key *</h3>
                    <input
                      type="text"
                      value={formData.key}
                      onChange={(e) => handleChange('key', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                        errors.key ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      placeholder="e.g., fiqh, aqeedah, seerah"
                      required
                    />
                    {errors.key && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.key}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">Unique identifier (lowercase, no spaces)</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Label *</h3>
                    <input
                      type="text"
                      value={formData.label}
                      onChange={(e) => handleChange('label', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                        errors.label ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      placeholder="e.g., Fiqh, Aqeedah, Seerah"
                      required
                    />
                    {errors.label && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.label}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">Display name shown to users</p>
                  </div>
                </div>

                {/* Description Field */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-content-text mb-4">Description</h3>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Brief description of this topic type..."
                  />
                  <p className="text-sm text-content-text-muted mt-2">{formData.description.length}/500 characters</p>
                </div>
              </form>
            </div>

            {/* Right Sidebar */}
            <div className="w-80 border-l border-border flex flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="topic-type-form"
                    disabled={isLoading || !formData.key.trim() || !formData.label.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Help Section */}
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  Help
                </h3>

                <div className="space-y-3 text-sm text-content-text-muted">
                  <p>• Key and label are required fields</p>
                  <p>• Key must be unique and lowercase</p>
                  <p>• Topic types categorize Community knowledge areas</p>
                  <p>• Examples: Fiqh, Aqeedah, Seerah, Tafseer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}



